import {t} from 'i18next';
import {Box, Typography} from '@mui/material';
import {type KeyboardEvent, useEffect, useRef, useState} from 'react';
import DeleteQuestionModal from './DeleteQuestionModal';
import DiveDeeper from '../DiveDeeper/DiveDeeper';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../shared/constants';
import SingleQuestion from './single-question/SingleQuestion';
import './QuestionAndAnswerPanel.scss';
import AnswerPanel from './answer-panel/AnswerPanel';
import ErrorBanner from './error-banner/ErrorBanner';
import {ConversationDto} from '../InvestigateChatPanel';
import DocPreviewModal from '../../../../document-view-prizmdoc/DocPreviewModal';
import type {DocForPreview} from '../../../../doc-explorer/doc-explorer-data-grid/DocExplorerDataGrid';
import {formatUtcToLongDate} from '../../../../../utils/formatters';
import ThinkingChip from '../../common/thinking-chip/ThinkingChip';
import React from 'react';

type ConversationsGroup = {
  creationDateKey: string;
  displayCreationDate: string;
  conversations: ConversationDto[];
};
export interface IQuestionAndAnswerPanelProps {
  conversations: ConversationDto[];
  setConversations: React.Dispatch<React.SetStateAction<ConversationDto[]>>;
  transcriptId?: number | string;
  indexId: number;
  loading: boolean;
  isTranscriptPanelCollapsed: boolean;
  readOnly?: boolean;
  transcriptOwnerName?: string;
  onSuggestedPromptSelect: (question: string) => void;
  isStreaming: boolean;
  onDeleteConversation?: (conversationId: string) => Promise<void>;
  isTranscriptLoading?: boolean;
}

const COLLAPSED_QA_MAX_WIDTH = 74.8; // in percent
const SCROLL_DELAY = 200;

const QuestionAndAnswerPanel = ({
  conversations,
  setConversations,
  transcriptId = 0,
  indexId,
  isTranscriptPanelCollapsed,
  readOnly = false,
  transcriptOwnerName = '',
  loading = false,
  isStreaming = false,
  onSuggestedPromptSelect,
  onDeleteConversation,
  isTranscriptLoading = false,
}: IQuestionAndAnswerPanelProps) => {
  const questionAndAnswerContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    null
  );
  const [showToast, setShowToast] = useState(false);
  const [selectedDocForPreview, setSelectedDocForPreview] =
    useState<DocForPreview | null>(null);
  const hasUserScrolledRef = useRef(false);
  const [conversationsGroups, setConversationsGroups] = useState<
    ConversationsGroup[]
  >([]);
  useEffect(() => {
    setConversationsGroups(getGroupConversationsByCreationDate(conversations));
  }, [conversations]);

  const getGroupConversationsByCreationDate = (
    conversations: ConversationDto[]
  ) => {
    const conversationsGroups: ConversationsGroup[] = [];
    conversations.forEach((conversation) => {
      const conversationGroupCreationDateKey = formatUtcToLongDate(
        conversation.createDate
      );
      let conversationsGroup: ConversationsGroup | undefined =
        conversationsGroups.find(
          (group) => group.creationDateKey === conversationGroupCreationDateKey
        );
      if (!conversationsGroup) {
        conversationsGroup = {
          creationDateKey: conversationGroupCreationDateKey,
          displayCreationDate: getDisplayDate(conversation.createDate),
          conversations: [],
        };
        conversationsGroups.push(conversationsGroup);
      }
      conversationsGroup.conversations.push(conversation);
    });
    return conversationsGroups;
  };

  const handleDeleteQuestionClick = (id: string) => {
    setSelectedQuestionId(id);
  };

  const handleCloseModal = () => {
    setSelectedQuestionId(null);
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      await onDeleteConversation?.(id);
      setConversations((prev) => {
        const que = [...prev];
        que.forEach((q) => {
          if (q.conversationId === id) {
            q.isRemoved = true;
          }
        });
        return que;
      });
      setShowToast(true);
    } catch {
      return;
    }
  };

  const handleDocIdLinkClick = (docId: string, documentId: number) => {
    setSelectedDocForPreview({
      id: documentId,
      docId,
      docName: '',
    });
  };
  useEffect(() => {
    // scroll to bottom if loading
    if (loading) {
      setTimeout(() => {
        const container = questionAndAnswerContainerRef.current;
        if (!container) return;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }, SCROLL_DELAY);
      return;
    }
    return () => {
      hasUserScrolledRef.current = false;
    };
  }, [loading]);

  useEffect(() => {
    if (isTranscriptLoading) return;
    if (hasUserScrolledRef.current) {
      if (!loading && !isStreaming) {
        hasUserScrolledRef.current = false;
      }
      return;
    }
    const container = questionAndAnswerContainerRef.current;
    if (!container) return;

    // scroll to starting position of the latest answer/error banner panel
    if (!loading) {
      setTimeout(() => {
        const errorBannerContainers =
          questionAndAnswerContainerRef.current?.querySelectorAll(
            '.error-banner-container'
          ) ?? [];
        if (errorBannerContainers.length > 0) {
          errorBannerContainers[0].scrollTo({
            top: errorBannerContainers[0].scrollHeight,
            behavior: 'smooth',
          });

          return;
        }
        const answerPanels =
          questionAndAnswerContainerRef.current?.querySelectorAll(
            '.answer-container'
          );
        if (!answerPanels || answerPanels.length === 0) return;
        const lastAnswerPanel = answerPanels[
          answerPanels.length - 1
        ] as HTMLDivElement | null;
        if (!lastAnswerPanel) return;
        lastAnswerPanel.scrollIntoView({block: 'start', behavior: 'smooth'});
      }, SCROLL_DELAY);
      return;
    }
  }, [loading, isTranscriptLoading]);

  const handleMouseWheel = () => {
    setUserScrolled();
  };

  const handleTouchMove = () => {
    setUserScrolled();
  };

  const handleKeyDown = (_event: KeyboardEvent<HTMLDivElement>) => {
    setUserScrolled();
  };
  const setUserScrolled = () => {
    if (!loading && !isStreaming) return;
    hasUserScrolledRef.current = true;
  };

  return (
    <Box
      className="q-and-a-container"
      data-testid="q-and-a-container"
      ref={questionAndAnswerContainerRef}
      onWheel={handleMouseWheel}
      onTouchMove={handleTouchMove}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      sx={{
        width: isTranscriptPanelCollapsed
          ? `${COLLAPSED_QA_MAX_WIDTH}%`
          : '100%',
      }}
    >
      <Box className="q-and-a-container-inner">
        {conversationsGroups.map((group, groupIndex) => {
          return (
            <Box key={groupIndex} className="conversation-group">
              <Typography
                data-testid="conversation-group-date"
                className="conversation-group-date typo-text label3_size"
              >
                {group.displayCreationDate}
              </Typography>

              <Box className="conversation-group-inner">
                {group.conversations.map((conversation, conversationIndex) => {
                  return (
                    <React.Fragment key={conversation.conversationId}>
                      {!conversation.isError && (
                        <Box className="question-scroll-anchor">
                          <SingleQuestion
                            key={conversation.conversationId}
                            question={conversation}
                            readOnly={readOnly}
                            isRemovable={!readOnly}
                            ownerName={transcriptOwnerName}
                            handleRemove={() =>
                              handleDeleteQuestionClick(
                                conversation.conversationId
                              )
                            }
                          />
                        </Box>
                      )}

                      {!conversation.isRemoved &&
                        group.conversations.length === conversationIndex + 1 &&
                        conversationsGroups.length === groupIndex + 1 &&
                        loading && (
                          <Box className="ai-thinking-chip-box">
                            <ThinkingChip visible={loading} />
                          </Box>
                        )}
                      {!conversation.isRemoved &&
                        !conversation.isError &&
                        conversation.answer.length > 0 && (
                          <AnswerPanel
                            answer={conversation.answer}
                            isStreaming={conversation.isStreaming}
                            conversationNumber={conversation.conversationNumber}
                            conversationId={conversation.conversationId}
                            transcriptId={transcriptId}
                            indexId={indexId}
                            onLinkClick={handleDocIdLinkClick}
                          />
                        )}
                      {!conversation.isRemoved &&
                        group.conversations.length === conversationIndex + 1 &&
                        !loading &&
                        group.conversations.length - 1 === conversationIndex &&
                        conversation.isError && (
                          <Box className="error-banner-container">
                            <ErrorBanner />
                          </Box>
                        )}

                      {!readOnly &&
                        !conversation.isRemoved &&
                        conversation.suggestedQuestions.length > 0 &&
                        group.conversations.length === conversationIndex + 1 &&
                        conversationsGroups.length === groupIndex + 1 && (
                          <Box className="dive-deeper-container">
                            <DiveDeeper
                              prompts={conversation.suggestedQuestions}
                              onSelectPrompt={onSuggestedPromptSelect}
                            />
                          </Box>
                        )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          );
        })}
        {readOnly && conversations.length > 0 && (
          <Typography
            className="transcript-end-text typo-text body4_size"
            data-testid="question-and-answer-panel-end-of-transcript"
          >
            {t('ai.investigate.questionAndAnswerPanel.endOfTranscript')}
          </Typography>
        )}
      </Box>
      <DeleteQuestionModal
        questionId={selectedQuestionId}
        open={Boolean(selectedQuestionId)}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        data-testid="delete-question-modal"
      />
      {showToast && (
        <ArrToastNotification
          message={t('ai.investigate.deleteQuestion.toastSuccess')}
          onClose={() => setShowToast(false)}
          status={[ToastType.Success]}
          data-testid="toast-message"
        />
      )}
      {!!selectedDocForPreview && (
        <DocPreviewModal
          showModal={!!selectedDocForPreview}
          onClose={() => setSelectedDocForPreview(null)}
          document={selectedDocForPreview}
        />
      )}
    </Box>
  );
};

export default QuestionAndAnswerPanel;

const getDisplayDate = (dateString: string): string => {
  const conversationDate = new Date(dateString);
  if (Number.isNaN(conversationDate.getTime())) {
    return '';
  }

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);
  const lastWeekStart = new Date(todayStart);
  lastWeekStart.setDate(todayStart.getDate() - 6);

  if (conversationDate >= todayStart) {
    return 'Today';
  }
  if (conversationDate >= yesterdayStart) {
    return 'Yesterday';
  }
  if (conversationDate >= lastWeekStart) {
    return formatUtcToLongDate(dateString, true);
  }

  return formatUtcToLongDate(dateString);
};
